/**
 * Photo marker detection utilities.
 *
 * Provides functions to detect photo marker entities by checking
 * their block table record name against known photo marker block names,
 * or by checking for xData tags on entity-mode markers.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { BLOCK_PREFIX } from "../blocks";
import { isBlockReference } from "../entities/detection";

// ============== Photo Marker Detection ==============

/** Photo marker block name prefix (after DR block prefix) */
export const PHOTO_MARKER_BLOCK_PREFIX = "PHOTO_";

/** Known photo marker block names */
export const PHOTO_MARKER_BLOCK_NAMES = [
  "PHOTO_CIRCLE",
  "PHOTO_LARGE_ARROW",
  "PHOTO_THIN_ARROW",
  "PHOTO_TEXT_ONLY",
];

// ============== xData Constants ==============

/** Registered application name used to tag entity-mode photo markers */
export const PHOTO_MARKER_XDATA_APP_NAME = "DR_PHOTO_MARKER";

// ============== xData Write ==============

/** Codes de groupe DXF du XData (1001 = nom d'application, 1000 = chaîne). */
const XDATA_APP = 1001;
const XDATA_STRING = 1000;

/**
 * Tag an entity as a photo marker.
 *
 * Only needed for entity-mode markers: block-mode ones are recognised by their
 * block name. The "text only" style is created in entity mode (a lone text, it
 * has no geometry to draw), so without this tag nothing distinguishes it from a
 * plain text — and the observations tool could not list it.
 */
export function writePhotoMarkerXData(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  shape: string,
): void {
  const entity = entityId.safeOpenObject(
    OpenMode.kForWrite,
    false,
  ) as DrawingModule.OdDbObject;

  // Toute allocation est libérée dans le finally : une exception ne doit jamais
  // laisser l'entité verrouillée en écriture (crash WASM au redraw suivant).
  let appStr: DrawingModule.OdString | null = null;
  let head: DrawingModule.OdResBuf | null = null;
  let shapeStr: DrawingModule.OdString | null = null;
  let shapeRb: DrawingModule.OdResBuf | null = null;

  try {
    const db = entity.database();
    appStr = new module.OdString(PHOTO_MARKER_XDATA_APP_NAME);
    db.newRegApp(appStr);

    head = module.OdResBuf.newRb(XDATA_APP);
    head.setString(appStr);

    shapeStr = new module.OdString(shape);
    shapeRb = module.OdResBuf.newRb(XDATA_STRING);
    shapeRb.setString(shapeStr);
    head.setNext(shapeRb);

    entity.setXData(head);
  } finally {
    if (shapeRb) deleteObj(shapeRb);
    if (head) deleteObj(head);
    if (shapeStr) deleteObj(shapeStr);
    if (appStr) deleteObj(appStr);
    deleteObj(entity);
  }
}

// ============== xData Detection ==============

/**
 * Check if an entity carries the photo marker xData tag.
 */
export function hasPhotoMarkerXData(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): boolean {
  let entity: DrawingModule.OdDbObject | null = null;
  let appNameStr: DrawingModule.OdString | null = null;
  let xdata: DrawingModule.OdResBuf | null = null;
  try {
    entity = entityId.safeOpenObject(OpenMode.kForRead, false);
    appNameStr = new module.OdString(PHOTO_MARKER_XDATA_APP_NAME);
    xdata = entity.xData1(appNameStr);
    return !!xdata;
  } finally {
    if (xdata) deleteObj(xdata);
    if (appNameStr) deleteObj(appNameStr);
    if (entity) deleteObj(entity);
  }
}

// ============== Combined Detection ==============

/**
 * Check if an entity is a photo marker.
 *
 * Checks two paths:
 * 1. Block-mode: OdDbBlockReference with a known photo marker block name
 * 2. Entity-mode: Any entity tagged with DR_PHOTO_MARKER xData
 */
export function isPhotoMarkerEntity(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): boolean {
  return getPhotoMarkerShape(module, entityId) !== null;
}

/**
 * Forme d'un marqueur photo, normalisée en minuscules ("circle",
 * "large_arrow", "thin_arrow", "text_only"), ou `null` si l'entité n'est pas
 * un marqueur. Mêmes deux chemins que la détection : nom de bloc (mode bloc)
 * ou XData DR_PHOTO_MARKER (mode entité — la forme y est stockée).
 *
 * La forme porte une sémantique terrain : les flèches pointent UN désordre,
 * les ronds servent souvent de photos de vue d'ensemble quand des flèches
 * existent sur le même plan (cf. analyse du plan).
 */
export function getPhotoMarkerShape(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): string | null {
  // Check 1: Block-mode (existing block reference check)
  if (isBlockReference(module, entityId)) {
    let pObj: DrawingModule.OdDbObject | null = null;
    let blockRef: DrawingModule.OdDbBlockReference | null = null;
    let blockRecord: DrawingModule.OdDbBlockTableRecord | null = null;
    try {
      pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
      blockRef = module.OdDbBlockReference.cast(pObj);
      if (blockRef) {
        const blockRecordId = blockRef.blockTableRecord();
        if (blockRecordId && !blockRecordId.isNull()) {
          blockRecord = openAs(
            blockRecordId,
            module.OdDbBlockTableRecord,
            OpenMode.kForRead,
          );
          if (blockRecord) {
            const nameStr = blockRecord.getName();
            const name = nameStr.toString();
            deleteObj(nameStr);

            const configName = name.startsWith(BLOCK_PREFIX)
              ? name.substring(BLOCK_PREFIX.length)
              : name;

            if (PHOTO_MARKER_BLOCK_NAMES.includes(configName)) {
              return configName
                .substring(PHOTO_MARKER_BLOCK_PREFIX.length)
                .toLowerCase();
            }
          }
        }
      }
    } finally {
      if (blockRecord) deleteObj(blockRecord);
      if (blockRef) deleteObj(blockRef);
      if (pObj) deleteObj(pObj);
    }
  }

  // Chemin 2 : mode entité — la forme est stockée dans le XData.
  return readPhotoMarkerXDataShape(module, entityId);
}

/**
 * Lit la forme stockée dans le XData DR_PHOTO_MARKER d'un marqueur en mode
 * entité, `null` si le tag est absent. Un tag présent mais sans forme (anciens
 * marqueurs) renvoie "entity".
 */
function readPhotoMarkerXDataShape(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): string | null {
  let entity: DrawingModule.OdDbObject | null = null;
  let appNameStr: DrawingModule.OdString | null = null;
  let head: DrawingModule.OdResBuf | null = null;
  let shapeRb: DrawingModule.OdResBuf | null = null;
  try {
    entity = entityId.safeOpenObject(OpenMode.kForRead, false);
    appNameStr = new module.OdString(PHOTO_MARKER_XDATA_APP_NAME);
    head = entity.xData1(appNameStr);
    if (!head) return null;
    // Chaîne : [1001] app → [1000] shape. Fin de chaîne = valeur falsy
    // (même piège que meta/xdata.ts).
    shapeRb = head.next();
    if (!shapeRb) return "entity";
    const shapeOd = shapeRb.getString();
    const shape = shapeOd.toString();
    deleteObj(shapeOd);
    return shape ? shape.toLowerCase() : "entity";
  } catch {
    return null;
  } finally {
    if (shapeRb) deleteObj(shapeRb);
    if (head) deleteObj(head);
    if (appNameStr) deleteObj(appNameStr);
    if (entity) deleteObj(entity);
  }
}
